import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppStore } from "../store/useAppStore.js";
import {
   STATUS_ORDER,
   STATUS_LABELS,
   Status,
   GoalMetric,
} from "@launchpad/shared";
import { ApplicationModal } from "../components/board/ApplicationModal.jsx";
import { GoalsCard } from "../components/dashboard/GoalsCard.jsx";

const STALE_DAYS = 14;
const ARCHIVE_DAYS = 30;

export function Dashboard() {
   const applications = useAppStore((s) => s.applications);
   const goals = useAppStore((s) => s.goals);
   const loading = useAppStore((s) => s.loading);
   const [selectedAppId, setSelectedAppId] = useState(null);
   const [onboardingDismissed, setOnboardingDismissed] = useState(
      () => localStorage.getItem("onboarded") === "1",
   );
   const navigate = useNavigate();

   const showOnboarding = !loading && !onboardingDismissed;

   function dismissOnboarding() {
      localStorage.setItem("onboarded", "1");
      setOnboardingDismissed(true);
      navigate("/board");
   }

   const metrics = useMemo(() => computeMetrics(applications), [applications]);
   const actions = useMemo(() => computeActions(applications), [applications]);
   const networkingSummary = useMemo(
      () => computeNetworkingSummary(applications),
      [applications],
   );
   const hasActions =
      actions.dueOverdue.length ||
      actions.followUp.length ||
      actions.needsNextStep.length ||
      actions.readyToArchive.length ||
      actions.undated.length ||
      actions.staleNetworking.length;
   const selectedApp = selectedAppId
      ? applications.find((a) => a.appId === selectedAppId)
      : null;

   return (
      <div className="p-6 space-y-6 max-w-5xl">
         <h1 className="text-lg font-semibold text-slate-900">Dashboard</h1>

         {/* Onboarding */}
         {showOnboarding && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
               <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4 text-center">
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">
                     Welcome to Launchpad 🚀
                  </h2>
                  <p className="text-sm text-slate-500 mb-6">
                     Your networking & job search command center.
                  </p>
                  <ul className="text-left text-sm text-slate-700 space-y-3 mb-8">
                     <li>
                        🤝 <strong>Track networking</strong> — build your
                        relationship pipeline from Target to Nurturing
                     </li>
                     <li>
                        🎯 <strong>Set weekly goals</strong> — outreach targets,
                        conversations, and application goals with streaks
                     </li>
                     <li>
                        📋 <strong>Track applications</strong> — Kanban board
                        from Applied to Offer, with one-click convert from
                        networking
                     </li>
                     <li>
                        ⚡ <strong>Stay accountable</strong> — stale
                        relationship alerts, overdue reminders, and a shareable
                        progress link for your coach
                     </li>
                  </ul>
                  <p className="text-xs text-slate-400 mb-4">
                     Start by adding your target companies to the Networking
                     board, or create your first application.
                  </p>
                  <button
                     onClick={dismissOnboarding}
                     className="rounded-lg bg-primary-600 px-6 py-3 text-sm font-semibold text-white hover:bg-primary-700 w-full"
                  >
                     Get Started
                  </button>
               </div>
            </div>
         )}

         {/* Metrics Cards */}
         <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <MetricCard
               label="Outreach This Week"
               value={networkingSummary.outreachThisWeek}
               highlight={networkingSummary.outreachThisWeek > 0}
            />
            <MetricCard
               label="Conversations"
               value={networkingSummary.scheduled + networkingSummary.nurturing}
            />
            <MetricCard label="Active Apps" value={metrics.active} />
            <MetricCard
               label="Due Today"
               value={metrics.dueToday}
               highlight={metrics.dueToday > 0}
            />
         </div>

         {/* Goals */}
         <GoalsCard />

         {/* Today's Actions */}
         {hasActions ? (
            <div className="rounded border border-slate-200 p-4 space-y-4">
               <h2 className="text-sm font-semibold text-slate-900">
                  Today's Actions
               </h2>

               {actions.staleNetworking.length > 0 && (
                  <ActionGroup
                     label="Follow up — stale relationship (14+ days)"
                     icon="🤝"
                     items={actions.staleNetworking}
                     onItemClick={(app) => setSelectedAppId(app.appId)}
                     accent="text-purple-600"
                  />
               )}

               {actions.dueOverdue.length > 0 && (
                  <ActionGroup
                     label="Due / Overdue"
                     icon="⚠"
                     items={actions.dueOverdue}
                     onItemClick={(app) => setSelectedAppId(app.appId)}
                     accent="text-red-600"
                  />
               )}

               {actions.followUp.length > 0 && (
                  <ActionGroup
                     label="Follow up (no activity 14+ days)"
                     icon="📬"
                     items={actions.followUp}
                     onItemClick={(app) => setSelectedAppId(app.appId)}
                     accent="text-amber-600"
                  />
               )}

               {actions.needsNextStep.length > 0 && (
                  <ActionGroup
                     label="Needs next step"
                     icon="→"
                     items={actions.needsNextStep}
                     onItemClick={(app) => setSelectedAppId(app.appId)}
                     accent="text-blue-600"
                  />
               )}

               {actions.readyToArchive.length > 0 && (
                  <ActionGroup
                     label="Ready to archive? (30+ days inactive)"
                     icon="📦"
                     items={actions.readyToArchive}
                     onItemClick={(app) => setSelectedAppId(app.appId)}
                     accent="text-slate-500"
                  />
               )}

               {actions.undated.length > 0 && (
                  <ActionGroup
                     label="Undated actions"
                     icon="📋"
                     items={actions.undated}
                     onItemClick={(app) => setSelectedAppId(app.appId)}
                     accent="text-slate-400"
                  />
               )}
            </div>
         ) : (
            <div className="rounded border border-slate-200 p-4">
               <p className="text-sm text-slate-400 italic">
                  ✓ No actions needed — you're all caught up
               </p>
            </div>
         )}

         {/* Networking Pipeline */}
         {networkingSummary.total > 0 && (
            <div className="rounded border border-slate-200 p-4">
               <h2 className="text-sm font-medium text-slate-700 mb-3">
                  🤝 Networking Pipeline
               </h2>
               <div className="flex items-center gap-1">
                  {[
                     {
                        label: "Target",
                        count: networkingSummary.targets,
                        color: "bg-slate-400",
                     },
                     {
                        label: "Outreach",
                        count: networkingSummary.outreach,
                        color: "bg-amber-400",
                     },
                     {
                        label: "Scheduled",
                        count: networkingSummary.scheduled,
                        color: "bg-emerald-400",
                     },
                     {
                        label: "Nurturing",
                        count: networkingSummary.nurturing,
                        color: "bg-purple-400",
                     },
                  ].map(({ label, count, color }) => (
                     <div key={label} className="flex-1 text-center">
                        <div
                           className={`mx-auto h-8 rounded ${color} flex items-center justify-center`}
                           style={{ opacity: count > 0 ? 1 : 0.3 }}
                        >
                           <span className="text-sm font-bold text-white">
                              {count}
                           </span>
                        </div>
                        <p className="text-[10px] text-slate-500 mt-1">
                           {label}
                        </p>
                     </div>
                  ))}
               </div>
            </div>
         )}

         {/* Weekly Progress */}
         <div className="rounded border border-slate-200 p-4">
            <h2 className="text-sm font-medium text-slate-700 mb-3">
               Weekly Applications (12 weeks)
            </h2>
            {metrics.weeklyData.length < 2 ? (
               <p className="text-sm text-slate-400 italic">
                  Not enough data yet
               </p>
            ) : (
               (() => {
                  const weeklyGoal = goals.find(
                     (g) =>
                        g.metric === GoalMetric.APPLICATIONS_SENT &&
                        g.period === "weekly",
                  );
                  const goalTarget = weeklyGoal?.target;
                  const maxVal = Math.max(
                     ...metrics.weeklyData,
                     goalTarget || 0,
                  );
                  return (
                     <div className="relative flex items-end gap-1 h-16">
                        {goalTarget && (
                           <div
                              className="absolute left-0 right-0 border-t border-dashed border-green-400"
                              style={{
                                 bottom: `${(goalTarget / maxVal) * 100}%`,
                              }}
                           >
                              <span className="absolute -top-3.5 right-0 text-[9px] text-green-600 font-medium">
                                 goal: {goalTarget}
                              </span>
                           </div>
                        )}
                        {metrics.weeklyData.map((count, i) => {
                           const metGoal = goalTarget && count >= goalTarget;
                           return (
                              <div
                                 key={i}
                                 className={`flex-1 rounded-t min-h-0.5 ${metGoal ? "bg-green-400" : "bg-primary-400"}`}
                                 style={{
                                    height: `${Math.max(4, (count / maxVal) * 100)}%`,
                                 }}
                                 title={`Week ${i + 1}: ${count}${goalTarget ? (metGoal ? " ✓" : " ✗") : ""}`}
                              />
                           );
                        })}
                     </div>
                  );
               })()
            )}
         </div>

         {/* Applications Summary */}
         {metrics.total > 0 && (
            <div className="rounded border border-slate-200 p-4">
               <h2 className="text-sm font-medium text-slate-700 mb-3">
                  Applications
               </h2>
               <div className="flex items-center gap-1">
                  {STATUS_ORDER.filter((s) => metrics.funnel[s] > 0).map(
                     (status) => {
                        const count = metrics.funnel[status];
                        const pct = (count / metrics.total) * 100;
                        return (
                           <div
                              key={status}
                              className="text-center"
                              style={{ flex: Math.max(pct, 8) }}
                           >
                              <div
                                 className="h-6 bg-primary-400 rounded flex items-center justify-center"
                                 style={{ opacity: 0.4 + (pct / 100) * 0.6 }}
                              >
                                 <span className="text-[10px] font-bold text-white">
                                    {count}
                                 </span>
                              </div>
                              <p className="text-[10px] text-slate-500 mt-1">
                                 {STATUS_LABELS[status]}
                              </p>
                           </div>
                        );
                     },
                  )}
               </div>
               <p className="text-xs text-slate-400 mt-2">
                  {metrics.total} total · {metrics.responseRate}% interview rate
               </p>
            </div>
         )}

         {selectedApp && (
            <ApplicationModal
               app={selectedApp}
               onClose={() => setSelectedAppId(null)}
            />
         )}
      </div>
   );
}

function ActionGroup({ label, icon, items, onItemClick, accent }) {
   return (
      <div>
         <p className={`text-xs font-medium ${accent} mb-1`}>
            {icon} {label}
         </p>
         <ul className="space-y-1">
            {items.map((app) => (
               <li key={app.appId}>
                  <button
                     onClick={() => onItemClick(app)}
                     className="w-full text-left rounded px-3 py-1.5 text-sm hover:bg-slate-50"
                  >
                     <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900 truncate">
                           {app.company}
                        </span>
                        <span className="text-slate-400">—</span>
                        <span className="text-slate-600 truncate">
                           {app.role}
                        </span>
                     </div>
                     {app.nextAction && (
                        <p className="text-xs text-slate-400 mt-0.5 truncate">
                           → {app.nextAction}
                        </p>
                     )}
                  </button>
               </li>
            ))}
         </ul>
      </div>
   );
}

function MetricCard({ label, value, highlight }) {
   return (
      <div
         className={`rounded border p-4 ${highlight ? "border-accent-400 bg-accent-50" : "border-slate-200"}`}
      >
         <p className="text-xs text-slate-500">{label}</p>
         <p
            className={`text-2xl font-bold ${value === 0 || value === "0%" ? "text-slate-400" : "text-slate-900"}`}
         >
            {value}
         </p>
      </div>
   );
}

function computeActions(applications) {
   const today = new Date().toISOString().slice(0, 10);
   const now = Date.now();

   // Exclude networking cards from application-specific actions
   const apps = applications.filter((a) => a.boardType !== "networking");

   // Due/overdue applies to ALL cards (networking + applications)
   const dueOverdue = applications.filter(
      (a) =>
         a.nextActionDate &&
         a.nextActionDate <= today &&
         a.status !== Status.REJECTED,
   );

   const followUp = apps.filter((a) => {
      if (a.status !== Status.APPLIED && a.status !== Status.INTERVIEWING)
         return false;
      if (a.nextActionDate) return false;
      const lastActivity = getLastActivityDate(a);
      if (!lastActivity) return false;
      return (now - lastActivity) / 86400000 >= STALE_DAYS;
   });

   const needsNextStep = apps.filter(
      (a) => a.status === Status.INTERVIEWING && !a.nextAction,
   );

   const readyToArchive = apps.filter((a) => {
      if (a.status !== Status.APPLIED) return false;
      if (a.nextActionDate) return false;
      const lastActivity = getLastActivityDate(a);
      if (!lastActivity) return false;
      return (now - lastActivity) / 86400000 >= ARCHIVE_DAYS;
   });

   const undated = apps.filter(
      (a) => a.nextAction && !a.nextActionDate && a.status !== Status.REJECTED,
   );

   // Networking: stale relationships (lastContacted > 14 days)
   const staleNetworking = applications.filter((a) => {
      if (a.boardType !== "networking") return false;
      if (!a.lastContacted) return false;
      return (
         (now - new Date(a.lastContacted).getTime()) / 86400000 >= STALE_DAYS
      );
   });

   return {
      dueOverdue,
      followUp,
      needsNextStep,
      readyToArchive,
      undated,
      staleNetworking,
   };
}

function getLastActivityDate(app) {
   if (app.statusHistory?.length > 0) {
      return new Date(
         app.statusHistory[app.statusHistory.length - 1].timestamp,
      ).getTime();
   }
   if (app.dateApplied) return new Date(app.dateApplied).getTime();
   return null;
}

function computeMetrics(applications) {
   // Exclude networking cards from pipeline metrics
   const apps = applications.filter((a) => a.boardType !== "networking");
   const total = apps.length;
   const active = apps.filter((a) => a.status !== Status.REJECTED).length;
   const interviewing = apps.filter(
      (a) => a.status === Status.INTERVIEWING,
   ).length;
   const beyondApplied = apps.filter(
      (a) => a.status === Status.INTERVIEWING || a.status === Status.OFFER,
   ).length;
   const responseRate =
      total > 0 ? Math.round((beyondApplied / total) * 100) : 0;

   const today = new Date().toISOString().slice(0, 10);
   const dueToday = apps.filter(
      (a) =>
         a.nextActionDate &&
         a.nextActionDate <= today &&
         a.status !== Status.REJECTED,
   ).length;

   const funnel = {};
   for (const s of STATUS_ORDER) funnel[s] = 0;
   for (const app of apps) {
      if (funnel[app.status] !== undefined) funnel[app.status]++;
   }

   const now = Date.now();
   const weeklyData = Array(12).fill(0);

   // Align to Monday-start weeks (matches goal periods)
   const nowDate = new Date();
   const dayOfWeek = nowDate.getDay();
   const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
   const thisMonday = new Date(nowDate.getFullYear(), nowDate.getMonth(), nowDate.getDate() - daysSinceMonday).getTime();

   for (const app of apps) {
      const dateStr = app.dateApplied || app.createdAt;
      if (!dateStr) continue;
      const appDate = new Date(dateStr).getTime();
      const weeksAgo = Math.floor((thisMonday - appDate) / (7 * 24 * 60 * 60 * 1000));
      if (appDate >= thisMonday) {
         weeklyData[11]++; // this week
      } else if (weeksAgo >= 0 && weeksAgo < 12) {
         weeklyData[11 - weeksAgo - 1]++;
      }
   }

   const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString();
   const weeklyDigest = [];
   for (const app of apps) {
      for (const entry of app.statusHistory || []) {
         if (entry.timestamp > weekAgo) weeklyDigest.push(entry);
      }
   }
   weeklyDigest.sort((a, b) => b.timestamp.localeCompare(a.timestamp));

   return {
      total,
      active,
      interviewing,
      responseRate,
      dueToday,
      funnel,
      weeklyData,
      weeklyDigest: weeklyDigest.slice(0, 10),
   };
}

function computeNetworkingSummary(applications) {
   const networking = applications.filter((a) => a.boardType === "networking");
   const total = networking.length;
   const targets = networking.filter(
      (a) => a.status === "target" || a.status === "researched",
   ).length;
   const outreach = networking.filter((a) => a.status === "outreach").length;
   const scheduled = networking.filter((a) => a.status === "scheduled").length;
   const nurturing = networking.filter((a) => a.status === "nurturing").length;

   const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
   let outreachThisWeek = 0;
   const outreachStatuses = new Set(["outreach", "scheduled", "nurturing"]);
   for (const app of networking) {
      // Only count if card is currently at outreach or beyond
      if (!outreachStatuses.has(app.status)) continue;
      for (const entry of app.statusHistory || []) {
         if (outreachStatuses.has(entry.to) && entry.timestamp > weekAgo) {
            outreachThisWeek++;
            break;
         }
      }
   }

   return { total, targets, outreach, scheduled, nurturing, outreachThisWeek };
}
